/**
 * Tutor Spaces — organization overview, workspaces, learner invites.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Organization, Workspace, WorkspaceInvite } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';

function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as any).results)) {
    return (data as any).results as T[];
  }
  return [];
}

export default function SpacesPage() {
  const [org, setOrg] = useState<Organization | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [selectedWs, setSelectedWs] = useState<number | ''>('');
  const [email, setEmail] = useState('');
  const [newWsName, setNewWsName] = useState('');
  const [instName, setInstName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lastLink, setLastLink] = useState<string | null>(null);

  const toast = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const ensured = await apiClient.ensurePersonalOrganization();
    if (!ensured.success || !ensured.data) {
      setError(ensured.error || 'Failed to load your space');
      setLoading(false);
      return;
    }
    const organization = ensured.data as Organization;
    setOrg(organization);

    const [wsRes, invRes] = await Promise.all([
      apiClient.getWorkspaces(organization.id),
      apiClient.getInvites(organization.id),
    ]);
    const ws = unwrapList<Workspace>(wsRes.data);
    setWorkspaces(ws);
    if (ws.length && selectedWs === '') setSelectedWs(ws[0].id);
    setInvites(unwrapList<WorkspaceInvite>(invRes.data));
    setLoading(false);
  }, [selectedWs]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createWorkspace = async () => {
    if (!org || !newWsName.trim()) return;
    setBusy(true);
    const res = await apiClient.createWorkspace({
      organization: org.id,
      name: newWsName.trim(),
    });
    setBusy(false);
    if (res.success) {
      setNewWsName('');
      toast('Workspace created.');
      load();
    } else setError(res.error || 'Failed to create workspace');
  };

  const createInvite = async () => {
    if (!org) return;
    setBusy(true);
    setLastLink(null);
    const res = await apiClient.createInvite({
      organization_id: org.id,
      workspace_id: selectedWs === '' ? null : Number(selectedWs),
      email: email.trim() || undefined,
      max_uses: email.trim() ? 1 : 0,
      expires_days: 30,
    });
    setBusy(false);
    if (res.success && res.data) {
      const inv = res.data as WorkspaceInvite;
      setLastLink(inv.accept_url);
      setEmail('');
      toast(inv.email_sent ? 'Invite created and email sent.' : 'Invite link ready.');
      load();
    } else setError(res.error || 'Failed to create invite');
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Invite link copied.');
    } catch {
      setError('Could not copy link');
    }
  };

  const revoke = async (id: number) => {
    if (!confirm('Revoke this invite?')) return;
    const res = await apiClient.revokeInvite(id);
    if (res.success) {
      toast('Invite revoked.');
      load();
    } else setError(res.error || 'Failed to revoke');
  };

  const createInstitution = async () => {
    if (!instName.trim()) return;
    setBusy(true);
    const res = await apiClient.createInstitution(instName.trim());
    setBusy(false);
    if (res.success && res.data) {
      setInstName('');
      toast('Institution created. Reloading personal space — open org list later for multi-org.');
      setOrg(res.data as Organization);
      load();
    } else setError(res.error || 'Failed to create institution');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Tenancy</p>
        <h1 className="mt-0.5 text-xl font-bold text-slate-900">Spaces</h1>
        <p className="text-sm text-slate-500 mt-1">
          Invite learners to your workspaces. Private courses only enroll members of selected spaces.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}

      {org && (
        <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{org.name}</h2>
              <p className="text-xs text-slate-400 mt-1">
                {org.org_type === 'institution' ? 'Institution' : 'Personal'} · role: {org.my_role || 'owner'}
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {workspaces.length} workspace{workspaces.length !== 1 ? 's' : ''}
            </span>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Workspaces</h2>
          <ul className="divide-y divide-slate-100">
            {workspaces.map(ws => (
              <li key={ws.id} className="py-2 flex justify-between text-sm">
                <span className="font-medium text-slate-800">{ws.name}</span>
                <span className="text-xs text-slate-400">{ws.members_count ?? 0} members</span>
              </li>
            ))}
            {workspaces.length === 0 && (
              <li className="text-sm text-slate-400 py-2">No workspaces yet.</li>
            )}
          </ul>
          <div className="flex gap-2">
            <input
              value={newWsName}
              onChange={e => setNewWsName(e.target.value)}
              placeholder="New workspace name"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={busy || !newWsName.trim()}
              onClick={createWorkspace}
              className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Invite learners</h2>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Workspace</label>
            <select
              value={selectedWs}
              onChange={e => setSelectedWs(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Org only (no workspace)</option>
              {workspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Email (optional)</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="learner@school.edu"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              With email: single-use invite (sent if SMTP is configured). Without: shareable link.
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={createInvite}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Create invite
          </button>
          {lastLink && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs break-all">
              <p className="font-medium text-slate-700 mb-1">Invite link</p>
              <p className="text-slate-600 mb-2">{lastLink}</p>
              <button
                type="button"
                onClick={() => copyLink(lastLink)}
                className="text-blue-600 font-semibold underline"
              >
                Copy link
              </button>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Recent invites</h2>
        {invites.length === 0 ? (
          <EmptyState compact icon="✉️" title="No invites yet." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {invites.slice(0, 20).map(inv => (
              <li key={inv.id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div>
                  <p className="font-medium text-slate-800">
                    {inv.workspace_name || 'Organization'} · {inv.status}
                  </p>
                  <p className="text-xs text-slate-400">
                    {inv.email || 'Link invite'} · uses {inv.use_count}
                    {inv.max_uses ? ` / ${inv.max_uses}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  {inv.status === 'pending' && (
                    <>
                      <button
                        type="button"
                        onClick={() => copyLink(inv.accept_url)}
                        className="rounded border border-slate-200 px-2 py-1 text-xs"
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => revoke(inv.id)}
                        className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-600"
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 space-y-3">
        <h2 className="text-base font-semibold text-slate-900">Create institution</h2>
        <p className="text-xs text-slate-500">
          For schools with multiple tutors. Your personal space stays available via ensure-personal.
        </p>
        <div className="flex gap-2">
          <input
            value={instName}
            onChange={e => setInstName(e.target.value)}
            placeholder="School or institution name"
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
          <button
            type="button"
            disabled={busy || !instName.trim()}
            onClick={createInstitution}
            className="rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
          >
            Create
          </button>
        </div>
      </section>
    </div>
  );
}
